import { useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ApiError } from '../../lib/api-client';
import { useResetPassword } from '../../lib/api';
import { AuthLayout } from './auth-layout';
import { Button, Field, FormError, TextInput } from '../../components/ui';

const schema = z
  .object({
    password: z.string().min(12, 'Use at least 12 characters.').max(200, 'Too long.'),
    confirmPassword: z.string(),
  })
  .refine((values) => values.password === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'The passwords do not match.',
  });

type ResetForm = z.infer<typeof schema>;

export function ResetPasswordRoute() {
  const { token } = useSearch({ strict: false });
  const reset = useResetPassword();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetForm>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit((values) => {
    if (token === undefined) return;
    setFormError(null);
    reset.mutate(
      { token, password: values.password },
      {
        // The server establishes a fresh session and revokes every other one, so the customer
        // lands in the application already signed in.
        onSuccess: () => void navigate({ to: '/overview' }),
        onError: (error) => {
          setFormError(error instanceof ApiError ? error.message : 'Could not reset the password.');
        },
      },
    );
  });

  if (token === undefined || token === '') {
    return (
      <AuthLayout
        title="Reset link required"
        footer={
          <Link to="/forgot-password" className="link">
            Request a new link
          </Link>
        }
      >
        <p className="text-sm text-neutral-700">
          Open the link from your password reset email to continue.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password">
      <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4" noValidate>
        <FormError message={formError} />

        <Field
          label="New password"
          htmlFor="password"
          hint="At least 12 characters."
          error={errors.password?.message}
        >
          <TextInput
            id="password"
            type="password"
            autoComplete="new-password"
            invalid={errors.password !== undefined}
            {...register('password')}
          />
        </Field>

        <Field
          label="Confirm password"
          htmlFor="confirmPassword"
          error={errors.confirmPassword?.message}
        >
          <TextInput
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            invalid={errors.confirmPassword !== undefined}
            {...register('confirmPassword')}
          />
        </Field>

        <p className="text-xs text-neutral-500">
          Every other signed-in session will be signed out.
        </p>

        <Button type="submit" disabled={isSubmitting || reset.isPending}>
          {reset.isPending ? 'Updating...' : 'Set new password'}
        </Button>
      </form>
    </AuthLayout>
  );
}
